import React, { useCallback, useEffect, useRef, useState } from "react";
import { nanoid } from "nanoid";
import { usePTS } from "../../../context/PTSContext";
import { useReportTemplateContext } from "../ReportTemplate/ReportTemplateContext";
import { useParams } from "react-router-dom";
import ConfirmModal from "../../ConfirmModal";
import { log } from "../../utils/generalConsoleLogger";
const EMPTY_SECTION = "__NONE__";

/* ------------------------------------------------------------
   ✅ Infer main category from activeSection.type
------------------------------------------------------------ */
function inferCategoryFromKey(key = "") {
  const lower = key.toLowerCase();

  // Use explicit prefix/exact matching — "lvi" appears as a substring in
  // "energiataloudellinen_selvitys" (se-lvi-tys), so .includes("lvi") would
  // give false positives. Match only standalone LVI section type names instead.
  if (/^lvi/.test(lower)) return "LVI Järjestelmät";
  if (/^(sahko|sähkö)/.test(lower)) return "Sähköjärjestelmät";
  if (/^(rakenne|rakennetekniikka)/.test(lower)) return "Rakennetekniikka";

  return "";
}

/* ------------------------------------------------------------
   ✅ Try to match subsection title into PTS dropdown sections
------------------------------------------------------------ */
function findClosestPTSSection(category, subsectionTitle, availablePTSSections) {
  if (!category || !subsectionTitle || category==='Lisätutkimukset') return EMPTY_SECTION;

  const sections = availablePTSSections?.[category] || [];

  const normSub = normalizeTitle(subsectionTitle);

  // 1. Exact normalized match
  const exact = sections.find(
    (s) => normalizeTitle(s) === normSub
  );

  if (exact) return exact;

  // 2. Loose normalized contains match
  const loose = sections.find((s) => {
    const normS = normalizeTitle(s);
    return normS.includes(normSub) || normSub.includes(normS);
  });

  return loose || EMPTY_SECTION;
}

/* ------------------------------------------------------------
   ✅ Normalize item with auto defaults
------------------------------------------------------------ */

function normalizeTitle(str = "") {
  return str
    .toLowerCase()
    .replace(/-/g, " ")              // hyphen → space
    .replace(/järjestelmät/g, "järjestelmä") // plural → singular
    .replace(/turvallisuus/g, "turva")       // normalize synonym
    .replace(/\bja\b/g, "")          // remove "ja"
    .replace(/[^\w\säöå]/g, "")      // remove punctuation
    .replace(/\s+/g, " ")            // collapse spaces
    .trim();
}

function normalizeItem(
  raw,
  activeCategoryKey,
  activeSubTitle,
  availablePTSSections
) {
  const ptsCategory =
    raw?.ptsCategory ?? inferCategoryFromKey(activeCategoryKey);

  const ptsSection =
    raw?.ptsSection ??
    findClosestPTSSection(ptsCategory, activeSubTitle, availablePTSSections);

  return {
    id: raw?.id ?? raw?.toimenpideId ?? nanoid(),
    text: raw?.text ?? "",

    ptsCategory,
    ptsSection,

    includeInPTS: !!raw?.includeInPTS,
    immediate: !!raw?.immediate,
    significant: !!raw?.significant,
    tutkimus: !!raw?.tutkimus,

    kl: raw?.kl || "",
  };
}

/* ------------------------------------------------------------
   ✅ Component
------------------------------------------------------------ */
export default function ToimenpideEhdotukset({
  data = {},
  onChange,
  blockPath = []
}) {
  const {
    availablePTSSections,
    updateTableKL,
    updateTableLabel,

    tekniikkaData,
    lviData,
    sahkoData,
    tutkimusData,

    setTekniikkaData,
    setSahkoData,
    setTutkimusData,
    setLviData,
    onRemoveFromPTS,
  } = usePTS();

  const { getParentSubsectionTitle, activeSection } = useReportTemplateContext();

  const { 
    id: kiinteistoId,        
    templateName,  
    reportId,                
    section,                 
    sectionId             
  } = useParams();

  /* ------------------------------------------------------------
     ✅ Get defaults from editor context
  ------------------------------------------------------------ */
  const activeCategoryKey = activeSection?.type || "";
  const activeSubTitle = getParentSubsectionTitle(blockPath);

  const [confirmModal, setConfirmModal] = useState({ show: false, rowIndex: null });


  /* ------------------------------------------------------------
     ✅ Local state
  ------------------------------------------------------------ */
  const [items, setItems] = useState(
    (data.items || []).map((it) =>
      normalizeItem(
        it,
        activeCategoryKey,
        activeSubTitle,
        availablePTSSections
      )
    )
  );

  //TÄMÄ UEF OTETTH POIS KÄYTÖSTÄ 26.03

  const lastExternalRef = useRef(null);

useEffect(() => {
  // console.groupCollapsed("🧠 items SYNC EFFECT TRIGGERED");

  // console.log("📌 Trigger reason (dependency changed): data.items");
  // console.log("📦 raw data.items:", data.items);

  if (!data.items) {
    console.log("⛔ EXIT: no data.items");
    console.groupEnd();
    return;
  }

  const serialized = JSON.stringify(data.items);

  // console.log("🧾 serialized incoming:", serialized);
  // console.log("🧠 lastExternalRef:", lastExternalRef.current);

  const isSameAsLastExternal =
    lastExternalRef.current === serialized;

  // console.log("🔍 external equality check:", isSameAsLastExternal);

  if (isSameAsLastExternal) {
    // console.log("🟡 SKIP: no external change (hydration guard hit)");
    // console.groupEnd();
    return;
  }

  // console.log("🔴 APPLYING external update to local state");

  lastExternalRef.current = serialized;

  const normalized = data.items.map((it, index) => {
    const result = normalizeItem(
      it,
      activeCategoryKey,
      activeSubTitle,
      availablePTSSections
    );

    // console.log("🔧 normalizeItem", {
    //   index,
    //   before: it,
    //   after: result
    // });

    return result;
  });

  // console.log("📤 setting local items:", normalized);

  setItems(normalized);

  console.groupEnd();
}, [activeSection.id]);


  const handleBlur = useCallback(() => {
    requestAnimationFrame(async () => {
      if (containerRef.current?.contains(document.activeElement)) {
        return; // still inside → ignore blur
      }

      log({ context: "toimenpideHandleblur went through!!!!" });

      if (!onChange) return;

      await onChange({
        ...data,
        items,
      });
    });
  }, [items, data, onChange]);

  /* ------------------------------------------------------------
     ✅ Sync from parent JSON changes
  ------------------------------------------------------------ */
    // useEffect(() => {
    //   setItems(
    //     (data.items || []).map((it) =>
    //       normalizeItem(
    //         it,
    //         activeCategoryKey,
    //         activeSubTitle,
    //         availablePTSSections
    //       )
    //     )
    //   );
    // }, [data.items, activeCategoryKey, activeSubTitle]);
  /* ------------------------------------------------------------
     ✅ Commit changes upward
  ------------------------------------------------------------ */
  // const commit = (next) => {
  //   setItems(next);
  //   onChange?.({ ...data, items: next });
  // };

  
    const commit = (next) => {
    setItems(next);
    onChange?.({ ...data, items: next });
  };

  // const commitToParent = (next) => {
  //   setItems(next);
  //   handleBlur()
  // }

  /* ------------------------------------------------------------
     ✅ Update one row
  ------------------------------------------------------------ */
  const updateItem = (idx, patch) => {
    const next = items.map((it, i) =>
      i === idx ? { ...it, ...patch } : it
    );

    commit(next);

    // Only trigger PTS sync when includeInPTS changes
  if (patch.includeInPTS === undefined && patch.text === undefined) return;

    const row = next[idx];
    const category = row.ptsCategory;
    const section = row.ptsSection;

    const dataMap = {
      Rakennetekniikka: tekniikkaData,
      "LVI Järjestelmät": lviData,
      Sähköjärjestelmät: sahkoData,
      Lisätutkimukset: tutkimusData,
    };

    const updaterMap = {
      Rakennetekniikka: setTekniikkaData,
      "LVI Järjestelmät": setLviData,
      Sähköjärjestelmät: setSahkoData,
      Lisätutkimukset: setTutkimusData,
    };

    const dataSet = dataMap[category];
    const updater = updaterMap[category];

    if (!dataSet) return;

    let sectionIdx = availablePTSSections?.[category]?.indexOf(section);


    if (category === "Lisätutkimukset") {
      sectionIdx = 0; 
    }

    if (sectionIdx === -1 || sectionIdx == null) return;

    const sectionData = dataSet[sectionIdx];
    if (!sectionData) return;

    /* ================= ADD / UPDATE ================= */
    if (patch.includeInPTS === true) {
          let targetRowIdx = sectionData.items.findIndex(
            (r) => r.metadata?.toimenpideId === row.id
          );

          // ➕ CREATE if not exists
          if (targetRowIdx === -1) {
            targetRowIdx = sectionData.items.length;

            sectionData.items.push({
              id: nanoid(),
              label: "",
              kl: "",
              values: Array(11).fill(0),
              metadata: {
                toimenpideId: row.id,
              },
            });
          }

          updater([...dataSet]);

          updateTableLabel(category, sectionIdx, targetRowIdx, row.text);
          updateTableKL(category, sectionIdx, targetRowIdx, row.kl || "");
      }

        /* ================= TEXT SYNC ================= */
        if (patch.text !== undefined && row.includeInPTS) {
          const targetRowIdx = sectionData.items.findIndex(
            (r) => r.metadata?.toimenpideId === row.id
          );

          if (targetRowIdx !== -1) {
            updateTableLabel(category, sectionIdx, targetRowIdx, row.text);
          }
        }

        /* ================= REMOVE ================= */
        if (patch.includeInPTS === false) {
          const targetRowIdx = sectionData.items.findIndex(
            (r) => r.metadata?.toimenpideId === row.id
          );

          if (targetRowIdx !== -1) {
            sectionData.items.splice(targetRowIdx, 1);
            updater([...dataSet]);
          }
        }
  };

  /* ------------------------------------------------------------
     ✅ Add row (auto-filled)
  ------------------------------------------------------------ */
  const addItem = () => {
    commit([
      ...items,
      normalizeItem(
        {},
        activeCategoryKey,
        activeSubTitle,
        availablePTSSections
      ),
    ]);
  };

  /* ------------------------------------------------------------
     ✅ Remove row
  ------------------------------------------------------------ */
const removeItem = (id) => {
  const row = items.find((r) => r.id === id);

  if (row?.includeInPTS) {
    alert("Poista PTS-valinta ennen rivin poistoa.");
    return;
  }

  const newItems = items.filter((r) => r.id !== id);

  // ✅ update local state
  setItems(newItems);

  // ✅ immediate commit (like bullet list fix)
  onChange?.({
    ...data,
    items: newItems,
  });
};

  /* ------------------------------------------------------------
     ✅ Render
  ------------------------------------------------------------ */
const containerRef = useRef(null);

return (
    <div
      ref={containerRef}
      // onBlur={(e) => {
      //   requestAnimationFrame(() => {
      //     if (!containerRef.current?.contains(document.activeElement)) {
      //       handleBlur();
      //     }
      //   });
      // }}
    >
    <div className="border rounded p-1 p-md-3 bg-white"

    >
      {items.map((row, i) => (
        <div key={row.id} className="mb-2 p-2 border rounded bg-light">

          {/* Textarea */}
          <textarea
            className="lato-textarea form-control mb-2"
            value={row.text}
            onChange={(e) => updateItem(i, { text: e.target.value })}
            onInput={(e) => {
              e.target.style.height = "auto";
              e.target.style.height = e.target.scrollHeight + "px";
            }}
            placeholder="Kirjoita toimenpide..."
            style={{ resize: "none", overflow: "hidden", fontSize: '0.875rem' }}
          />

          {/* Dropdowns — always side by side */}
          <div className="row g-1 mb-2">
            <div className="col-6">
              <select
                value={row.ptsCategory}
                onChange={(e) => {
                  if (row.includeInPTS || row.tutkimus) {
                    alert("Poista toimenpide ensin PTS:stä ja Lisätutkimuksista ennen kategorian vaihtoa.");
                    return;
                  }
                  updateItem(i, { ptsCategory: e.target.value, ptsSection: EMPTY_SECTION });
                }}
                className="form-select"
                style={{ fontSize: '0.875rem' }}
              >
                <option value="">Kategoria</option>
                {Object.keys(availablePTSSections || {}).map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="col-6">
              <select
                value={row.ptsSection}
                onChange={(e) => {
                  if (row.includeInPTS || row.tutkimus) {
                    alert("Poista toimenpide ensin PTS:stä ja Lisätutkimuksista ennen osion vaihtoa.");
                    return;
                  }
                  updateItem(i, { ptsSection: e.target.value });
                }}
                className="form-select"
                style={{ fontSize: '0.875rem' }}
              >
                <option value={EMPTY_SECTION} disabled>Osio</option>
                {(availablePTSSections[row.ptsCategory] || []).map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Flags + PTS + delete — single row */}
          <div
            className="d-flex flex-wrap align-items-center justify-content-center gap-2"
            style={{ fontSize: '0.8rem' }}
          >
            <label className="text-danger d-flex align-items-center gap-1 mb-0">
              <input type="checkbox" data-skip-blur="true" checked={row.immediate}
                onChange={(e) => updateItem(i, { immediate: e.target.checked })} />
              Välitön
            </label>

            {templateName === "wk3" && (
              <label className="text-warning d-flex align-items-center gap-1 mb-0">
                <input type="checkbox" data-skip-blur="true" checked={row.significant}
                  onChange={(e) => updateItem(i, { significant: e.target.checked })} />
                Merkittävä
              </label>
            )}

            <label className="text-info d-flex align-items-center gap-1 mb-0">
              <input type="checkbox" checked={row.tutkimus}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  if (!isChecked && row.includeInPTS) {
                    alert("Poista toimenpide ensin PTS:stä ennen Lisätutkimuksen poistamista.");
                    return;
                  }
                  if (isChecked) {
                    updateItem(i, { tutkimus: true, ptsCategory: "Lisätutkimukset", ptsSection: "Tutkimustarpeet", includeInPTS: true });
                  } else {
                    updateItem(i, { tutkimus: false, includeInPTS: false });
                  }
                }} />
              Lisätutkimus
            </label>

            <div className="d-flex gap-1 ms-auto ms-md-3 flex-shrink-0">
              <button
                type="button"
                disabled={!row.includeInPTS && (!row.ptsCategory || row.ptsSection === EMPTY_SECTION || !row.text.trim())}
                className={`btn btn-sm ${
                  row.includeInPTS ? "btn-success"
                  : (!row.ptsCategory || row.ptsSection === EMPTY_SECTION || !row.text.trim()) ? "btn-outline-secondary"
                  : "btn-outline-primary"
                }`}
                onClick={() => updateItem(i, { includeInPTS: !row.includeInPTS })}
              >
                {row.includeInPTS ? "✓ PTS" : "+ PTS"}
              </button>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => {
                  if (row.includeInPTS) {
                    alert("Poista PTS-valinta ennen rivin poistoa.");
                  } else {
                    setConfirmModal({ show: true, rowId: row.id });
                  }
                }}
                title="Poista toimenpide"
              >×</button>
            </div>
          </div>
        </div>
      ))}

      {/* Add new row */}
      <div className="text-end mt-2">
        <button className="btn btn-outline-secondary btn-sm" onClick={addItem}>
          + lisää toimenpide
        </button>
      </div>
    </div>

    {/* Confirm Modal */}
    <ConfirmModal
      show={confirmModal?.show}
      title="Poista rivi"
      message="Haluatko varmasti poistaa tämän toimenpiteen?"
      confirmText="Poista"
      cancelText="Peruuta"
      confirmVariant="danger"
      onClose={() => setConfirmModal({ show: false, rowId: null })}
      onConfirm={() => {
        if (confirmModal?.rowId != null) {
          removeItem(confirmModal.rowId);
          setConfirmModal({ show: false, rowId: null });
        }
      }}
    />

          {/* <div className="mt-4">
        <h6>Active Block Content (debug)</h6>
        <pre>{JSON.stringify(data, null, 2)}</pre>
      </div> */}
  </div>
);





}
